import React, { useMemo, useState } from "react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Input } from "../ui/input";
import {
  User, Building, CreditCard, Phone, Briefcase, Wrench,
  Sparkles, PhoneOutgoing, Crown, Zap, Bell, Palette,
  BookOpen, MapPin, Mail, ShieldCheck, Database, Plug, FileText, Search, CornerDownRight, ListChecks,
} from "lucide-react";
import { SETTINGS_CARDS } from "./settingsSearchRegistry";

// Single registry powering both the desktop sidebar and the mobile dropdown.
// `show` gates items by role/account type; order defines the visual order.
const TAB_GROUPS = [
  {
    label: "Account",
    items: [
      { value: "my-account", label: "My Account", icon: User, show: (f) => !f.isDispatcher },
      { value: "my-plan", label: "My Plan", icon: Crown, show: (f) => !f.isTeamMember },
    ],
  },
  {
    label: "Business Setup",
    items: [
      { value: "business", label: "Business Info", icon: Building },
      { value: "branding", label: "Branding", icon: Palette },
      { value: "services", label: "Services", icon: Wrench },
      { value: "sources", label: "Job Sources", icon: Briefcase },
      { value: "job-statuses", label: "Job Statuses", icon: ListChecks },
      { value: "store-locations", label: "Store Locations", icon: MapPin, show: (f) => f.isStoreAccount && !f.isDispatcher },
    ],
  },
  {
    label: "Communication",
    items: [
      { value: "phones", label: "Phone Numbers", icon: Phone, show: (f) => !f.isDispatcher },
      { value: "call-masking", label: "Call Masking", icon: PhoneOutgoing, show: (f) => !f.isDispatcher },
      { value: "notifications", label: "Notifications", icon: Bell },
      { value: "email", label: "Email", icon: Mail, show: (f) => !f.isDispatcher },
      { value: "templates", label: "Invite Templates", icon: Sparkles, show: (f) => !f.isDispatcher },
    ],
  },
  {
    label: "Payments & Billing",
    items: [
      { value: "payments", label: "Payment Types", icon: CreditCard },
      { value: "stripe-connect", label: "Stripe Connect", icon: Zap, show: (f) => !f.isDispatcher },
      { value: "quickbooks", label: "QuickBooks", icon: BookOpen, show: (f) => !f.isDispatcher },
    ],
  },
  {
    label: "Documents & Reports",
    items: [
      { value: "pdf-reports", label: "PDF Reports", icon: FileText, show: (f) => !f.isDispatcher },
      { value: "compliance", label: "Compliance", icon: ShieldCheck, show: (f) => !f.isDispatcher },
    ],
  },
  {
    label: "Data & Integrations",
    items: [
      { value: "data-import", label: "Data Import", icon: Database, show: (f) => !f.isDispatcher },
      { value: "integrations", label: "Integrations", icon: Plug, show: (f) => !f.isDispatcher },
    ],
  },
];

const SettingsTabBar = ({ activeTab, setActiveTab, isTeamMember, isDispatcher, isStoreAccount }) => {
  const flags = { isTeamMember, isDispatcher, isStoreAccount };
  const [query, setQuery] = useState("");

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TAB_GROUPS
      .map((g) => ({
        ...g,
        items: g.items.filter((it) => (!it.show || it.show(flags)) && (!q || it.label.toLowerCase().includes(q))),
      }))
      .filter((g) => g.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, isTeamMember, isDispatcher, isStoreAccount]);

  const allItems = TAB_GROUPS.flatMap((g) => g.items).filter((it) => !it.show || it.show(flags));

  const tabLabels = useMemo(() => Object.fromEntries(allItems.map((it) => [it.value, it.label])), [allItems]);

  const cardMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return SETTINGS_CARDS
      .filter((c) => tabLabels[c.tab] && (c.label.toLowerCase().includes(q) || c.keywords.includes(q)))
      .slice(0, 8);
  }, [query, tabLabels]);

  const jumpToCard = (card) => {
    setActiveTab(card.tab);
    let tries = 0;
    const tryScroll = () => {
      const el = document.querySelector(`[data-testid="${card.testid}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("settings-jump-highlight");
        setTimeout(() => el.classList.remove("settings-jump-highlight"), 2000);
      } else if (tries++ < 20) {
        setTimeout(tryScroll, 100);
      }
    };
    setTimeout(tryScroll, 60);
  };

  return (
    <>
      {/* Mobile dropdown (unchanged UX) */}
      <div className="sm:hidden">
        <Select value={activeTab} onValueChange={setActiveTab}>
          <SelectTrigger className="input-glass" data-testid="settings-mobile-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {allItems.map((it) => (
              <SelectItem key={it.value} value={it.value}>
                <div className="flex items-center gap-2"><it.icon className="w-4 h-4" /> {it.label}</div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Desktop sidebar — grouped sections, sticky, with quick filter */}
      <aside className="hidden sm:block w-56 lg:w-60 shrink-0" data-testid="settings-sidebar">
        <div className="sm:sticky sm:top-4 rounded-xl border border-theme bg-theme-surface/60 backdrop-blur-sm p-2.5 max-h-[calc(100vh-6rem)] overflow-y-auto scrollbar-thin">
          <div className="relative mb-2">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search settings…"
              className="h-8 pl-8 text-sm bg-background/60 border-theme"
              data-testid="settings-search-input"
            />
          </div>
          {groups.length === 0 && cardMatches.length === 0 && (
            <p className="text-xs text-muted-foreground px-2 py-3" data-testid="settings-search-empty">No settings match “{query}”</p>
          )}
          <nav className="space-y-3">
            {groups.map((g) => (
              <div key={g.label}>
                <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 select-none">{g.label}</p>
                <div className="space-y-0.5">
                  {g.items.map((it) => {
                    const active = activeTab === it.value;
                    return (
                      <button
                        key={it.value}
                        type="button"
                        onClick={() => setActiveTab(it.value)}
                        data-testid={`tab-${it.value}`}
                        data-value={it.value}
                        className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-sm text-left transition-colors ${
                          active
                            ? "bg-[#FFCC00] text-black font-medium shadow-sm"
                            : "text-muted-foreground hover:text-foreground hover:bg-theme-hover"
                        }`}
                      >
                        <it.icon className={`w-4 h-4 shrink-0 ${active ? "text-black" : ""}`} />
                        <span className="truncate">{it.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
          {cardMatches.length > 0 && (
            <div className="mt-3 pt-2 border-t border-theme" data-testid="settings-jump-results">
              <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 select-none">Jump to</p>
              <div className="space-y-0.5">
                {cardMatches.map((c) => (
                  <button
                    key={`${c.tab}-${c.testid}`}
                    type="button"
                    onClick={() => jumpToCard(c)}
                    data-testid={`jump-${c.testid}`}
                    className="w-full flex items-start gap-2 px-2.5 py-1.5 rounded-lg text-left text-muted-foreground hover:text-foreground hover:bg-theme-hover transition-colors"
                  >
                    <CornerDownRight className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#FFCC00]" />
                    <span className="min-w-0">
                      <span className="block text-sm truncate">{c.label}</span>
                      <span className="block text-[10px] text-muted-foreground/70 truncate">{tabLabels[c.tab]}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default SettingsTabBar;
